import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { setPreference, usePreference } from '@fs/zion-preferences'
import { DARK_MODE_VIEWS, DEFAULT_VIEW } from '../../components/viewManager/views'

const DARK_MODE_PREFERENCE_KEY = 'shared-image-viewer.darkMode'

// The user preference makes dark mode follow the user across browsers, but it loads asynchronously.
// localStorage caches the last known value so a refresh renders the right theme on the first paint
// instead of flashing light mode while the preference is fetched.
function readCachedDarkMode() {
  try {
    const cached = window.localStorage.getItem(DARK_MODE_PREFERENCE_KEY)
    if (cached === 'true') return true
    if (cached === 'false') return false
  } catch (error) {
    // localStorage can throw when storage is disabled (e.g. some private browsing modes)
  }
  return undefined
}

function writeCachedDarkMode(isOn) {
  try {
    window.localStorage.setItem(DARK_MODE_PREFERENCE_KEY, String(isOn))
  } catch (error) {
    // Caching is best effort; the user preference is still the source of truth
  }
}

// Same shape as the route that renders ViewManager (App.js), so a NotFound URL like /ark:/61903/X/extra doesn't match
const VIEW_MANAGER_PATH = /\/ark:\/61903\/[^/]+\/?$/

// Pages opt in to dark mode by mounting, which happens after the loading skeleton has already painted.
// Working out from the URL whether the page will allow dark mode lets the first paint use the right theme.
function isDarkModeAllowedOnUrl() {
  const { pathname, search } = window.location
  const view = new URLSearchParams(search).get('view') || DEFAULT_VIEW
  return VIEW_MANAGER_PATH.test(pathname) && DARK_MODE_VIEWS.has(view)
}

const DarkModeContext = createContext(undefined)

export default function DarkModeProvider({ children }) {
  const [isDarkModeOn, setIsDarkModeOn] = useState(() => readCachedDarkMode() ?? false)
  const [isDarkModeAllowedOnPage, setIsDarkModeAllowedOnPage] = useState(isDarkModeAllowedOnUrl)
  const darkModePreference = usePreference(DARK_MODE_PREFERENCE_KEY)
  // Once the user changes the setting here, a preference response that arrives later is stale
  const hasUserChangedDarkMode = useRef(false)

  // Sync with the user preference once it loads, in case it was changed in another browser.
  // '' means the preference was never set; null means there is no user session, so keep the cache.
  useEffect(() => {
    if (hasUserChangedDarkMode.current || darkModePreference === undefined || darkModePreference === null) return
    const isOn = darkModePreference === true
    setIsDarkModeOn(isOn)
    writeCachedDarkMode(isOn)
  }, [darkModePreference])

  const setDarkMode = useCallback(async (isOn) => {
    hasUserChangedDarkMode.current = true
    setIsDarkModeOn(isOn)
    writeCachedDarkMode(isOn)

    try {
      await setPreference(DARK_MODE_PREFERENCE_KEY, isOn)
    } catch (error) {
      console.error('Failed to persist dark mode preference', error)
    }
  }, [])

  const resetDarkMode = useCallback(async () => {
    hasUserChangedDarkMode.current = true
    setIsDarkModeOn(false)
    writeCachedDarkMode(false)

    try {
      await setPreference(DARK_MODE_PREFERENCE_KEY, false)
    } catch (error) {
      console.error('Failed to reset dark mode preference', error)
    }
  }, [])

  const theme = isDarkModeOn && isDarkModeAllowedOnPage ? 'night' : 'day'

  const value = useMemo(
    () => ({
      isDarkModeOn,
      setIsDarkModeOn: setDarkMode,
      resetDarkMode,
      theme,
      setIsDarkModeAllowedOnPage,
    }),
    [isDarkModeOn, resetDarkMode, setDarkMode, theme]
  )

  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>
}

export function useDarkModeContext() {
  const context = useContext(DarkModeContext)
  if (!context) {
    throw new Error(
      'useDarkModeContext must be used within a DarkModeProvider. If writing a test, use <StateProvider> from testUtils.js'
    )
  }
  return context
}

export { DarkModeContext }
